import React, { ChangeEvent, FC, memo, useState } from "react";

import StickyButton from "@/common/StickyButton";
import {
    logAnalyticsEvent,
    logPaltaAnalyticsEvent,
    setAnalyticsUserProperties,
    setPaltaAnalyticsUserProperties,
} from "@/constants/amplitude";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import { sendMetrics } from "@/constants/gtag";
import { Question } from "@/types/question";
import useAnswers from "@/utils/useAnswers";

interface QuestionProps {
    question: Question;
    handleNext(answ: unknown): void;
    isActive: boolean;
}

const createRangeStyles = ({ value = 3, min = 1, max = 10 }) => {
    return {
        background: `linear-gradient(to right, #EC5929 0%, #EC5929 ${
            ((+value - +min) / (+max - +min)) * 100
        }%, #F3F4F7 ${((+value - +min) / (+max - +min)) * 100}%, #F3F4F7 100%)`,
    };
};

const RangeNumber: FC<QuestionProps> = memo(({ question, handleNext }) => {
    const { answers } = useAnswers();
    const [selectedValue, setSelectedValue] = useState<string>(
        (answers[question.id] as string) ?? question?.defaultValue?.toString() ?? ""
    );

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        setSelectedValue(e.target.value);
    };

    const handleContinue = () => {
        if (["web_period_days", "web_cycle_days"].includes(question.id)) {
            logPaltaAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
                [WebEventProps.SCREEN]: question.event,
                [WebEventProps.RESULT]: selectedValue,
            });
            setPaltaAnalyticsUserProperties({ [question.id]: selectedValue });
            logAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
                [WebEventProps.SCREEN]: question.event,
            });
            sendMetrics(WebEventTypes.RESULT_EVENT, { [WebEventProps.SCREEN]: question.event });
        } else {
            logAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
                [WebEventProps.SCREEN]: question.event,
                [WebEventProps.RESULT]: selectedValue,
            });
            setAnalyticsUserProperties({ [question.id]: selectedValue });
            sendMetrics(WebEventTypes.RESULT_EVENT, {
                [WebEventProps.SCREEN]: question.event,
                [WebEventProps.RESULT]: selectedValue,
            });
        }
        handleNext({ [question.id]: selectedValue });
    };

    return (
        <div className="flex h-full flex-col justify-between gap-10">
            <div className="flex flex-col gap-4">
                <div className="whitespace-pre-line text-zing-24-32 font-semibold text-white female:text-black">
                    {question?.title}
                </div>
                <div>{question?.subtitle}</div>
            </div>
            <div className="flex flex-col gap-4">
                <div className="text-center text-zing-40-48 font-semibold">{`${selectedValue} days`}</div>
                <div>
                    <input
                        name="range"
                        type="range"
                        className="range-number h-2 w-full rounded-lg bg-zing-warm-tangerine-500"
                        style={createRangeStyles({
                            value: +selectedValue,
                            min: question?.rangeMin,
                            max: question?.rangeMax,
                        })}
                        min={question?.rangeMin}
                        max={question?.rangeMax}
                        value={selectedValue}
                        onChange={handleChange}
                    />
                </div>
                <div className="flex justify-between text-zing-16-22 font-semibold">
                    <span>{question?.rangeMin}</span>
                    <span>{question?.rangeMax}</span>
                </div>
            </div>
            <StickyButton onClick={handleContinue} btnClassName="bg-black">
                Continue
            </StickyButton>
        </div>
    );
});

export default RangeNumber;
